import { Component, Inject, OnInit, HostListener } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from "@angular/material/dialog";
import { PropertyService } from "../../../services/property.services";
import { Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap
} from "rxjs/operators";
import { environment } from "../../../../../environments/environment";
import { MatSnackBar } from "@angular/material/snack-bar";
import { I18nService } from "../../../services";
import { MasterTenantsService } from "../../../services/master-tenants.service";
import { ConfirmAliasFieldsComponent } from "./confirm-alias-fields/confirm-alias-fields.component";
import { LangSelectorPipe } from "../../../pipes/lang-selector.pipe";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-edit-detail",
  templateUrl: "./edit-detail.component.html",
  styleUrls: ["./edit-detail.component.scss"]
})
export class EditDetailComponent implements OnInit {
  detailTenantForm: FormGroup;
  errorMessage = false;
  tenants: any;
  floorOrWarehouseControl: string;
  moduleOrSuiteControl: string;
  isIndustry;
  autocompleteResults = [];
  private searchSubject = new Subject<string>();
  isLoading = false;
  isDropdownOpen = false;
  dropdownClicked = false;
  currentCode: string;
  isCreate: any;
  listOwners = [];
  autocompleteOwnersResults = [];
  private searchOwnerSubject = new Subject<string>();
  isDropdownOwnerOpen = false;
  dropdownOwnerClicked = false;
  isLoadingOwner = false;
  id;
  changeFlieds;

  constructor(
    public dialogRef: MatDialogRef<EditDetailComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      data;
      buildingType;
      isAdmin;
      includedInQuarterlyStats;
      isCreate;
      industrialStatus;
    },
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private snackBar: MatSnackBar,
    private i18: I18nService,
    private masterTenantsService: MasterTenantsService,
    private dialog: MatDialog,
    private langSelectorPipe: LangSelectorPipe
  ) {
    this.currentCode = environment.platformId;
    this.isIndustry = this.data?.buildingType === 1001;
    this.isCreate = this.data?.isCreate;
    this.id = this.data.industrialStatus.id;
    this.initDetailTenantForm();
  }

  ngOnInit(): void {
    if (this.data?.data) {
      this.detailTenantForm.patchValue(this.data?.data);
      if (!this.data.data.tenantName) {
        this.detailTenantForm
          .get("tenantName")
          .setValue(
            this.langSelectorPipe.transform(
              this.data.data.tenantNameOption.name
            )
          );
      }
    }

    if (
      this.currentCode == "br" ||
      (this.currentCode == "mx" && this.isCreate)
    ) {
      this.masterTenantsService.getCorporations().then((resp: any) => {
        this.listOwners = resp;

        this.searchOwnerSubject
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map((term: string) => {
              this.isLoadingOwner = true;
              return this.filterOwners(term);
            })
          )
          .subscribe((results: any[]) => {
            this.autocompleteOwnersResults = results;
            this.isLoadingOwner = false;
          });
      });
    }

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          this.isLoading = true;
          return this.propertyService.fetchTenantsAutocompleteResults(term);
        })
      )
      .subscribe((results: any[]) => {
        this.autocompleteResults = results;
        this.autocompleteResults.push(
          {
            tenantName: this.langSelectorPipe.transform(
              this.selectVacant("Vacant")
            )
          },
          {
            tenantName: this.langSelectorPipe.transform(
              this.selectVacant("Unknown")
            )
          }
        );
        this.isLoading = false;
      });
  }

  selectVacant(tenantName) {
    switch (tenantName) {
      case "Vacant":
        return `{"pt-br":"Vago","en":"Vacant"}`;
      case "Unknown":
        return `{"pt-br":"Desconhecido","en":"Unknown"}`;
    }
  }

  filterOwners(term: string): any[] {
    if (!term) {
      return [];
    }

    term = term.toLowerCase();

    return this.listOwners.filter(owner =>
      owner.name.toLowerCase().includes(term)
    );
  }

  initDetailTenantForm() {
    this.detailTenantForm = this.fb.group({
      ...(!this.isCreate ? { id: new FormControl() } : {}),
      ...(this.isIndustry
        ? {
            warehouse: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              Validators.required
            )
          }
        : {}),
      ...(this.isIndustry && !this.isCreate
        ? { warehouseAlias: new FormControl() }
        : {}),
      ...(!this.isIndustry
        ? {
            floor: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              Validators.required
            )
          }
        : {}),
      ...(!this.isIndustry && !this.isCreate
        ? { floorAlias: new FormControl() }
        : {}),
      ...(this.isIndustry
        ? {
            module: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              Validators.required
            )
          }
        : {}),
      ...(this.isIndustry && !this.isCreate
        ? { moduleAlias: new FormControl() }
        : {}),
      ...(!this.isIndustry
        ? {
            suiteNumber: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              Validators.required
            )
          }
        : {}),
      ...(!this.isIndustry && !this.isCreate
        ? { suiteNumberAlias: new FormControl() }
        : {}),
      area: new FormControl({
        value: null,
        disabled: this.validateField()
      }),
      ...(!this.isIndustry && this.currentCode == "br"
        ? {
            bomaArea: new FormControl({
              value: null,
              disabled: this.validateField()
            })
          }
        : {}),
      ...(this.isIndustry ? { moduleEfficiency: new FormControl() } : {}),
      ...(this.isIndustry ? { docksPerModule: new FormControl() } : {}),
      tenantName: new FormControl({
        value: null,
        disabled: this.validateField()
      }),
      owners: new FormControl(),
      ownersGroup: new FormControl(),
      ownersContactName: new FormControl(),
      ownersContactEmail: new FormControl(null, Validators.email),
      ownersContactPhone: new FormControl()
    });
  }

  save() {
    if (this.detailTenantForm.valid) {
      if (!this.isCreate && this.validateChangesAliasField()) {
        this.confirmSaveAliasFields();
      } else {
        this.dialogRef.close(this.detailTenantForm.value);
      }
    } else {
      if (this.detailTenantForm.get("ownersContactEmail").invalid) {
        this.showMessage(this.i18.get("detail-tenant.messageEmail"));
      } else {
        if (this.isIndustry) {
          this.showMessage(this.i18.get("detail-tenant.messageIndustrial"));
        } else {
          this.showMessage(this.i18.get("detail-tenant.messageOffice"));
        }
      }
    }
  }

  close() {
    this.dialogRef.close();
  }

  onInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchSubject.next(input);
    this.isDropdownOpen = true;
  }

  onInputChangeOwners(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchOwnerSubject.next(input);
    this.isDropdownOpen = true;
  }

  onSelectOption(option: any): void {
    this.detailTenantForm.get("tenantName")?.setValue(option.tenantName);
    this.autocompleteResults = [];
    this.isDropdownOpen = false;
  }

  onSelectOwnerOption(option: any): void {
    this.detailTenantForm.get("owners")?.setValue(option.name);
    this.autocompleteOwnersResults = [];
    this.isDropdownOwnerOpen = false;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const clickedInside =
      (event.target as HTMLElement).closest(".tenant-input") ||
      (event.target as HTMLElement).closest(".autocomplete-dropdown");
    if (!clickedInside) {
      this.isDropdownOpen = false;
    }
  }

  showMessage(message) {
    this.snackBar.open(this.i18.get(message), "Close", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["custom-snackbar"]
    });
  }

  confirmSaveAliasFields() {
    const dialogRef = this.dialog.open(ConfirmAliasFieldsComponent, {
      width: "400px",
      panelClass: "alias-fields",
      data: {
        changeFlieds: this.changeFlieds
      }
    });

    dialogRef.afterClosed().subscribe((confirm: any) => {
      if (confirm) {
        this.dialogRef.close(this.detailTenantForm.value);
      }

      if (confirm == false) {
        this.changeFlieds.forEach(field => {
          const controlAlias = field.includes("Alias")
            ? this.detailTenantForm.get(field)
            : null;
          if (controlAlias) {
            controlAlias.reset();
          }
          const control = !field.includes("Alias")
            ? this.detailTenantForm.get(field)
            : null;
          if (control) {
            control.setValue(this.data.data[field]);
          }
        });
      }
    });
  }

  validateField() {
    return (
      this.data.includedInQuarterlyStats && (this.id == 1 || this.id == 1001)
    );
  }

  validateChangesAliasField() {
    const fieldsToCheck = [
      "floor",
      "suiteNumber",
      "warehouse",
      "module",
      "floorAlias",
      "suiteNumberAlias",
      "warehouseAlias",
      "moduleAlias"
    ] as const;

    const current = this.detailTenantForm.value;
    this.changeFlieds = fieldsToCheck.filter(
      key => this.data?.data[key] != current[key] && current[key] != null
    );

    return this.changeFlieds.length > 0 ? true : false;
  }

  delete(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      width: "400px",
      disableClose: true,
      autoFocus: true,
      panelClass: ["confirmation-dialog-custom-delete"],
      data: {
        title: this.i18.get("detail-tenant.confirmDelete"),
        message: this.i18.get("detail-tenant.confirmDeleteMessage"),
        confirmLabel: this.i18.get("detail.yes"),
        cancelLabel: this.i18.get("detail.no"),
        onConfirm: () => {
          this.dialogRef.close({ deleted: true });
        }
      }
    });
  }
}
